/**
 * Failure diagnostics for remote configuration fetches.
 *
 * Why this module exists
 * ----------------------
 * Every config-fetch failure used to arrive in Sentry as the same opaque string:
 * `Failed to Fetch <url>: Status=0`. The information needed to tell a CDN outage
 * from a flapping client network was available at the moment of failure and then
 * thrown away - `HttpClient` distinguished `xhr.onerror` from `xhr.ontimeout` and
 * then flattened both into the literal `0`.
 *
 * This module collects the signals that survive that boundary, derives a single
 * failure class from them, and packages the result as Sentry tags (searchable,
 * chartable) plus contexts (detail). The classifier is pure so the whole decision
 * table is testable without mocks.
 *
 * Resilience contract (REFERENCE.md §11)
 * -----------------------------------
 * Diagnostics must never be the reason a report is lost. Every collector is
 * independently guarded and degrades field-by-field: a broken signal omits its
 * own field, it never omits the report and never throws onto the host page.
 * Browser APIs are reached by attempt-and-catch, never by a `typeof` probe
 * (invariant #2 - reading a stubbed getter can itself throw).
 *
 * Privacy
 * -------
 * Nothing derived from a response body is ever transmitted. Only its length and
 * a single boolean (`bodyLooksHtml`) leave the page, and every URL is reduced to
 * origin+pathname by `stripQuery` before it reaches Sentry, because a client's
 * form thank-you page can carry personal data in its query string.
 */

import {
    TimerBases,
    coerceNumber,
    coerceString,
    safeNumberOrNull,
    elapsedMs,
    safeConnectionInfo,
    safeLocationHostname,
    safeNavigatorOnLine,
    safeUrlHostname,
    safeVisibilityState,
    startTimer,
} from './safeglobals';
import { TelemetryScopeData } from './telemetry';
import { ENVIRONMENT } from './environment';
import { VERSION } from './version';

/**
 * Last-resort probe URLs, used only when the bundle's own location cannot be read.
 *
 * These are a GUESS about where the bundle is hosted, and a wrong guess is worse
 * than no probe: an <img> fires `error` for a host that does not serve the object
 * exactly as it does for a dead network, so every failure in that environment would
 * classify as `network-down`. Prefer `probeUrl()`, which derives the real location.
 */
const FALLBACK_PROBE_URL = ENVIRONMENT === 'nonprod'
    // nonprod publishes to lib/dev/<sha>/, which the bundle cannot know; every main push
    // moves the rolling pointer, so that is where this build's probe is
    ? 'https://nonprod-web.fonterra.io/lib/v' + VERSION.split('.')[0] + '/probe.gif'
    // production: this build's own exact-version directory, which every release has. The
    // rolling pointer is not moved by a prerelease tag (analytics-release.yml), so for a
    // prerelease it may be missing or hold another build, and a probe there would read as
    // network-down.
    : 'https://web.fonterra.io/lib/' + VERSION + '/probe.gif';

/**
 * URL of the 1x1 control GIF: `probe.gif`, resolved against the directory the
 * running bundle was actually loaded from.
 *
 * Derived, not hardcoded, because the bundle is served from more than one origin -
 * the custom domain in one environment and `storage.googleapis.com/<bucket>` in
 * another (compare the `allowUrls` list in ./telemetry) - and a hardcoded host is
 * wrong for all but one of them. webpack emits `probe.gif` into the output dir
 * beside `fountain.js` (see webpack.config.js) and both release paths preserve
 * that pairing, so "same directory as the running script" is correct for every
 * origin, bucket and versioned path without knowing any of them.
 *
 * This is also what makes the probe meaningful: the question it answers is "can this
 * page still reach the place it loaded us from?", which only works if we ask the
 * place it actually loaded us from.
 *
 * `document.currentScript` is read at module-evaluation time - the bundle body runs
 * synchronously during its own <script> execution, which is exactly when that
 * property is set. It is null for a bundle injected by other means, and `URL` is
 * absent on IE11, so both degrade to FALLBACK_PROBE_URL.
 *
 * It is an IMAGE, and loaded via `new Image()`, on purpose. An <img> load is a
 * no-cors request - the same request class as the <script src> that loaded this
 * bundle - so it needs no Access-Control-Allow-Origin header and therefore no
 * bucket CORS policy. An XHR or fetch() probe would be CORS-gated and would report
 * failure on a perfectly healthy network until such a policy existed, which is
 * exactly the kind of confidently-wrong signal this module exists to eliminate.
 */
export const CONTROL_PROBE_URL = bundleRelativeUrl('probe.gif');

/**
 * Resolve a filename against the directory the running bundle was loaded from,
 * falling back to FALLBACK_PROBE_URL when that location cannot be determined.
 *
 * Guarded end-to-end: this runs at module load on every page view, including the
 * happy path, so it must never throw onto the host page.
 */
function bundleRelativeUrl(filename: string) : string {
    let scriptUrl = '';
    try {
        const script = (document as any).currentScript;
        scriptUrl = coerceString(script ? script.src : '');
    } catch (ignored) {
        // currentScript stubbed by an extension; fall through to the default
    }
    if (!scriptUrl) {
        return FALLBACK_PROBE_URL;
    }
    // Cut at the last '/' to get the directory, after dropping any query/fragment
    // that would otherwise be treated as part of the filename.
    const path = stripQuery(scriptUrl);
    const lastSlash = path.lastIndexOf('/');
    if (lastSlash === -1) {
        return FALLBACK_PROBE_URL;
    }
    return path.substring(0, lastSlash + 1) + filename;
}

/** Budget for the control probe. Deliberately short: it runs after a failure. */
export const CONTROL_PROBE_TIMEOUT_MS = 3000;

/**
 * Below this elapsed time a failure cannot have involved a network round trip,
 * so it indicates the request was refused locally (CSP, extension) rather than
 * attempted. Also used to recognise an instant CORS rejection of the probe.
 */
const NO_ROUND_TRIP_MS = 50;

/**
 * Which XHR settle path fired. `throw` means open()/send() threw synchronously.
 * `none` means no attempt was recorded at all - used when the failure did not
 * come from HttpClient (a parse or shape rejection), so that absence is stated
 * rather than guessed at.
 */
export type XhrHandler = 'load' | 'error' | 'timeout' | 'abort' | 'throw' | 'none';

/** What a single HTTP attempt did. One of these per attempt, including retries. */
export interface HttpAttempt {
    n: number;
    handler: XhrHandler;
    status: number;
    readyState: number;
    responseURL: string;
    ms: number;
    delayMs: number;
    bytes: number;
    bodyLooksHtml: boolean;
    /**
     * For handler === 'throw', the thrown error's `.name` only (e.g. 'SecurityError'
     * for a CSP block vs 'InvalidStateError' for a malformed URL). '' otherwise.
     * Never the message, which could contain a URL.
     */
    thrownName: string;
    visStart: string;
    visEnd: string;
}

/** The attempt log carried out of HttpClient on the thrown Error. */
export interface HttpFailure {
    url: string;
    attempts: HttpAttempt[];
}

export type ProbeResult = 'ok' | 'failed' | 'blocked' | 'skipped';

/**
 * Which tier of the network -> cache -> default chain ended up serving.
 *
 * There is no 'none' tier: a fallback is always supplied, so the chain always ends
 * in something usable. See fetchJsonConfig for why that is deliberate.
 */
export type Degradation = 'cache' | 'default';

export type FailureClass =
    | 'offline'
    | 'network-down'
    | 'timeout'
    | 'blocked-csp'
    | 'request-rejected'
    | 'no-round-trip'
    | 'cors-or-blocked'
    | 'http-4xx'
    | 'http-5xx'
    | 'truncated-body'
    | 'intercepted'
    | 'bad-shape'
    | 'aborted'
    | 'unknown';

/**
 * Resource Timing phase breakdown for one request.
 *
 * `opaque` is the field that matters most in practice. The config URL is
 * cross-origin to every client page, and without a `Timing-Allow-Origin`
 * response header the browser zeroes every phase boundary, `transferSize` and
 * `nextHopProtocol`. When `opaque` is true only `startTime`, `duration` and
 * `responseEnd` carry information, and no rule may read a zeroed phase as evidence
 * of anything - which is why `opaque` gates the one rule that reads `transferSize`.
 */
export interface ResourceTimingSummary {
    opaque: boolean;
    startTime: number;
    duration: number;
    responseEnd: number;
    dns: number;
    tcp: number;
    tls: number;
    ttfb: number;
    download: number;
    /**
     * Bytes on the wire, or null when unreadable.
     *
     * Nullable where the other fields are not, because this is the only one a rule
     * treats as EVIDENCE (rule 12: a completed round trip that carried nothing back
     * means CORS or a blocking extension). Collapsing an unreadable getter to 0 let
     * one hostile field fabricate that verdict, which is the same mistake rule 13
     * used to make with missing timing data.
     *
     * A measured 0 is common and meaningful: the browser zeroes it for every
     * cross-origin entry without `Timing-Allow-Origin`, which is exactly why the rule
     * is also gated on `opaque`.
     */
    transferSize: number | null;
    encodedBodySize: number;
    nextHopProtocol: string;
}

/** Flat input to the classifier. No globals, so the table is testable directly. */
export interface FailureSignals {
    handler: XhrHandler;
    /**
     * For handler === 'throw', the thrown DOMException's `.name`. '' when unreadable
     * or when no synchronous throw occurred.
     *
     * Load-bearing, not decoration: without it every synchronous throw was reported as
     * `blocked-csp`, so a malformed config URL ('SyntaxError') arrived as a confident
     * accusation about the client's Content-Security-Policy. HttpAttempt has captured
     * this distinction all along; it simply never reached the classifier.
     */
    thrownName: string;
    status: number;
    elapsedMs: number;
    /**
     * Whether the response body started with '<', or null when the body could not be
     * read at all.
     *
     * Nullable because rules 2 and 3 read it as evidence, and `false` would otherwise
     * mean both "we read the body and it was not HTML" and "we never saw the body" -
     * so an unreadable body was reported as `truncated-body`, a claim about content
     * nobody observed. The unknown state already existed one layer down as
     * `HttpAttempt.bytes === -1`; it simply never reached here.
     */
    bodyLooksHtml: boolean | null;
    redirected: boolean;
    online: boolean | null;
    visibility: string;
    timing: ResourceTimingSummary | null;
    probe: ProbeResult;
    parseFailed: boolean;
    shapeFailed: boolean;
}

/** Property used to smuggle the attempt log out on a plain Error. */
const FAILURE_PROPERTY = '__fountainHttpFailure';
/** Marker set by the caller when its own shape validation rejected a response. */
export const SHAPE_FAILURE_PROPERTY = '__fountainShapeFailed';

/**
 * Drop the query string, fragment, and any userinfo (user:pass@) from a URL.
 *
 * Applied to every URL before it reaches Sentry - message text, Error messages,
 * tags and contexts alike. Config URLs and `responseURL` values can both carry
 * personal data in their query strings, and a query string would also make the
 * value high-cardinality and useless as a tag.
 *
 * Deliberately a string cut, NOT the `URL` constructor, for three reasons:
 * 1. Relative URLs (legal for XHR, e.g. '/wp-content/.../config.json') must keep
 *    their path - `new URL()` throws on them, and returning '' loses the URL from
 *    every report for that client.
 * 2. `URL` is absent on IE11; the cut works everywhere.
 * 3. It is identity for a clean absolute URL - `new URL(u).origin + pathname`
 *    would append a trailing '/' to a bare-origin URL and normalise case, which
 *    would destabilise message strings that embed the URL.
 *
 * Returns '' only for non-string input.
 */
export function stripQuery(url: string) : string {
    try {
        if (typeof url !== 'string') {
            return '';
        }
        let cut = url;
        const query = cut.indexOf('?');
        if (query !== -1) {
            cut = cut.substring(0, query);
        }
        const fragment = cut.indexOf('#');
        if (fragment !== -1) {
            cut = cut.substring(0, fragment);
        }
        // drop credentials embedded in the authority: scheme://user:pass@host/...
        // Two things the naive form got wrong:
        //  - `[^/]*@` is greedy to the LAST '@' before the path, so a credential
        //    containing an unencoded '@' is removed whole. `[^/@]*@` stopped at the
        //    first one and left the tail ('http://user:p@ss@h/x' -> 'http://ss@h/x').
        //  - the scheme is OPTIONAL. A protocol-relative URL ('//host/config.json') is
        //    a legal XHR input, and requiring 'scheme://' meant '//user:secret@host/x'
        //    kept its credentials all the way into the Sentry message.
        // Requiring '//' still means a path containing '@' is left alone, since that
        // is a path segment and not userinfo.
        return cut.replace(/^([a-z][a-z0-9+.-]*:)?(\/\/)[^/]*@/i, '$1$2');
    } catch (ignored) {
        return '';
    }
}

/**
 * Guarded read of one property off an untrustworthy object, coerced to a number
 * (0 when unreadable) or a string ('' when unreadable).
 *
 * One try/catch per read, which is the whole contract: these are used for Resource
 * Timing boundaries and XHR fields, where a single stubbed getter must cost its own
 * field and never the surrounding summary.
 *
 * The property arrives as a string because the objects indexed here are `any` by
 * necessity - a stubbed global's declared type is not trustworthy - so there is no
 * type safety to preserve by passing a typed accessor instead.
 */
export function safeNumberOf(source: any, property: string) : number {
    const value = safeNumberOrNull(source, property);
    return value === null ? 0 : value;
}

export function safeStringOf(source: any, property: string) : string {
    try {
        return coerceString(source ? source[property] : '');
    } catch (ignored) {
        return '';
    }
}

/**
 * Final URL after redirects, query-stripped. '' when unreadable (absent on IE11).
 *
 * Lives here rather than in ./safeglobals because that module's contract is browser
 * *globals*, and an XHR instance is per-request state; and because the redaction
 * this applies - and `HttpAttempt.responseURL`, which consumes it - are both defined
 * in this file. It takes the xhr as a parameter rather than closing over one so it
 * is a plain function (invariant #10) and directly testable.
 */
export function safeResponseUrl(xhr: XMLHttpRequest | null | undefined) : string {
    const url = safeStringOf(xhr, 'responseURL');
    return url ? stripQuery(url) : '';
}

/**
 * Response body text, or null when unreadable.
 *
 * null is distinct from '': it means we could not see the body at all, which is why
 * `HttpAttempt.bytes` reports -1 rather than 0 in that case. The body itself never
 * leaves the page - only its length and the `bodyLooksHtml` boolean derived from it.
 */
export function safeResponseText(xhr: XMLHttpRequest | null | undefined) : string | null {
    try {
        // ONE read, then inspect the value. `typeof xhr.responseText === 'string'
        // ? xhr.responseText : …` reads the getter TWICE - a probe followed by a
        // read - which invariant #2 forbids precisely because the getter is the
        // untrustworthy part: a stub can return a different body the second time,
        // and the attempt log would then describe a body the caller never sees.
        const body = xhr ? xhr.responseText : null;
        return typeof body === 'string' ? body : null;
    } catch (ignored) {
        return null;
    }
}

/** The `.name` of a thrown value ('SecurityError', 'InvalidStateError'), never its
 *  message, which could carry a URL. '' when unreadable. */
export function safeErrorName(error: any) : string {
    return safeStringOf(error, 'name');
}

/**
 * Attach an attempt log to an Error without subclassing it.
 *
 * Deliberately NOT `class HttpFailureError extends Error`. Under `target: es5`
 * TypeScript's `__extends` emits `return _super.apply(this, arguments) || this`,
 * and `Error.apply(this)` returns a new truthy Error, so the constructor returns
 * that instead of `this` and `instanceof` breaks. `Object.setPrototypeOf` would
 * repair it but is itself absent on the old browsers this library supports.
 *
 * That matters because src/main.ts branches on `error instanceof Error` in three
 * places to decide whether to forward the error to telemetry - a broken prototype
 * chain would silently drop every failure into the no-stack path, which is the
 * exact bug this module exists to fix.
 */
export function attachHttpFailure(error: any, failure: HttpFailure) : void {
    try {
        error[FAILURE_PROPERTY] = failure;
    } catch (ignored) {
        // a frozen or sealed Error is not worth failing a report over
    }
}

/** Guarded read of the attempt log off a thrown value. Never throws. */
export function httpFailureOf(error: any) : HttpFailure | null {
    try {
        const failure = error ? error[FAILURE_PROPERTY] : null;
        return failure && failure.attempts ? failure as HttpFailure : null;
    } catch (ignored) {
        return null;
    }
}

/**
 * Record a completed-but-unsuccessful response as a single-attempt log.
 *
 * Needed because HttpClient deliberately RETURNS 404 and 401 rather than throwing
 * (they will not change on retry), so no attempt log accompanies them. Without
 * this the report would carry status 0 and a conclusive 4xx would be misclassified
 * as a transport failure.
 */
export function attachStatusFailure(
    error: any,
    url: string,
    response: { status: number; url?: string; redirected?: boolean; attempts?: HttpAttempt[] },
) : void {
    // Prefer the REAL attempt log the response carries: it holds the true status, the
    // body-shape flag, the measured durations, and any retries that preceded this
    // response. Fabricating a record here would erase all of that - a fetch that
    // network-errored twice before a 404 would report a single instant attempt.
    let attempts: HttpAttempt[] = [];
    try {
        if (response && response.attempts && response.attempts.length) {
            attempts = response.attempts;
        }
    } catch (ignored) {
        attempts = [];
    }
    if (!attempts.length) {
        // Last resort (e.g. a stubbed HttpClient in tests): record the one fact we
        // do know, the status, so a conclusive 4xx is not misread as a transport failure.
        attempts = [{
            n: 1,
            handler: 'load',
            status: safeNumberOf(response, 'status'),
            readyState: 4,
            responseURL: safeStringOf(response, 'url'),
            ms: -1,
            delayMs: 0,
            bytes: -1,
            bodyLooksHtml: false,
            thrownName: '',
            visStart: safeVisibilityState(),
            visEnd: safeVisibilityState(),
        }];
    }
    attachHttpFailure(error, { url: stripQuery(url), attempts: attempts });
}

/** Mark an Error as "we fetched and parsed it, but the shape was wrong". */
export function markShapeFailure(error: any) : void {
    try {
        error[SHAPE_FAILURE_PROPERTY] = true;
    } catch (ignored) {
        // see attachHttpFailure
    }
}

function isShapeFailure(error: any) : boolean {
    try {
        return !!(error && error[SHAPE_FAILURE_PROPERTY]);
    } catch (ignored) {
        return false;
    }
}

/**
 * True when the error came from `JSON.parse` rejecting the response body.
 *
 * Matched on `.name` rather than `instanceof SyntaxError` so it still works
 * across realm boundaries (an iframe or a stubbed global would break instanceof).
 */
function isParseFailure(error: any) : boolean {
    try {
        return !!(error && error.name === 'SyntaxError');
    } catch (ignored) {
        return false;
    }
}

/**
 * Summarise the Resource Timing entry for a URL, or null when unavailable.
 *
 * Uses a buffered `getEntriesByName` lookup at failure time rather than a
 * `PerformanceObserver`: it costs nothing on the happy path and needs no
 * lifetime management. The API is absent on older browsers and can be stubbed,
 * so the whole body is guarded.
 *
 * The LAST matching entry is taken - retries produce one entry per attempt under
 * the same name, and the final attempt is the one being reported.
 *
 * Absence of an entry is itself a signal, not just missing data: a CSP
 * `connect-src` violation is blocked before the fetch is made and produces no
 * entry at all, whereas a CORS rejection produces one with a non-zero duration
 * and a zero `transferSize`.
 */
export function collectResourceTiming(url: string) : ResourceTimingSummary | null {
    try {
        const entry = lastTimingEntry(url);
        if (!entry) {
            return null;
        }
        // Every boundary is read through safeNumberOf, which owns a try/catch per
        // read. That per-field guarding is the point: one hostile getter must cost
        // its own field and nothing else. An earlier helper here took the already-
        // evaluated value, so the read happened at the call site outside any guard -
        // one throwing getter then hit the outer try and lost the entire summary,
        // which also manufactures the `timing: absent` signal the classifier reads
        // as evidence of a CSP block.
        const domainLookupStart = safeNumberOf(entry, 'domainLookupStart');
        const domainLookupEnd = safeNumberOf(entry, 'domainLookupEnd');
        const connectStart = safeNumberOf(entry, 'connectStart');
        const connectEnd = safeNumberOf(entry, 'connectEnd');
        const secureConnectionStart = safeNumberOf(entry, 'secureConnectionStart');
        const requestStart = safeNumberOf(entry, 'requestStart');
        const responseStart = safeNumberOf(entry, 'responseStart');
        const responseEnd = safeNumberOf(entry, 'responseEnd');
        const duration = safeNumberOf(entry, 'duration');
        // Cross-origin without Timing-Allow-Origin: the browser reports the entry
        // but zeroes every phase boundary. Detect it so the classifier can skip
        // the phase-based rules instead of misreading zeros as evidence.
        const opaque = domainLookupStart === 0
            && connectStart === 0
            && requestStart === 0
            && duration > 0;
        return {
            opaque: opaque,
            startTime: safeNumberOf(entry, 'startTime'),
            duration: duration,
            responseEnd: responseEnd,
            // Clamped: a boundary whose getter threw degrades to 0, and 0 minus a
            // real start value is a NEGATIVE duration - nonsense to whoever reads
            // the report. Field-by-field degradation must produce a missing phase,
            // not an impossible one.
            dns: phase(domainLookupEnd - domainLookupStart),
            tcp: phase(connectEnd - connectStart),
            tls: secureConnectionStart > 0 ? phase(connectEnd - secureConnectionStart) : 0,
            ttfb: responseStart > 0 && requestStart > 0 ? phase(responseStart - requestStart) : 0,
            download: responseEnd > 0 && responseStart > 0 ? phase(responseEnd - responseStart) : 0,
            transferSize: safeNumberOrNull(entry, 'transferSize'),
            encodedBodySize: safeNumberOf(entry, 'encodedBodySize'),
            nextHopProtocol: safeStringOf(entry, 'nextHopProtocol'),
        };
    } catch (ignored) {
        return null;
    }
}

/**
 * The last Resource Timing entry for a URL, or null when there is none.
 *
 * `PerformanceResourceTiming.name` is the RESOLVED absolute URL, so a relative config
 * URL - which HttpClient explicitly supports, e.g. '/wp-content/.../config.json' -
 * never matches its own entry when looked up verbatim. That mattered beyond losing
 * detail: absence of an entry is itself a classifier signal, so every such client
 * reported `timing: absent`, and a fast failure was then misread as a local block.
 *
 * The query string is preserved, because it is part of the entry name.
 *
 * Tries the resolved form first and the caller's string second, so an absolute URL
 * that already matched cannot regress if `URL` normalises it differently to whatever
 * the browser recorded.
 *
 * The LAST match is taken - retries produce one entry per attempt under the same
 * name, and the final attempt is the one being reported.
 */
function lastTimingEntry(url: string) : any {
    const candidates = [absoluteUrl(url), url];
    for (let i = 0; i < candidates.length; i ++) {
        const candidate = candidates[i];
        if (!candidate || (i > 0 && candidate === candidates[0])) {
            continue;
        }
        const entries = window.performance.getEntriesByName(candidate, 'resource');
        if (entries && entries.length) {
            return entries[entries.length - 1] as any;
        }
    }
    return null;
}

/**
 * Resolve a possibly-relative URL the way the browser did when it made the request:
 * against the document base, which honours a <base> tag. '' when it cannot be
 * resolved - `URL` is absent on IE11 - so callers fall back to the raw string.
 */
export function absoluteUrl(url: string) : string {
    try {
        const base = coerceString((document as any).baseURI) || window.location.href;
        return new URL(url, base).href;
    } catch (ignored) {
        return '';
    }
}

/**
 * The hostname a request was actually sent to, resolving relative and
 * protocol-relative URLs first.
 *
 * `safeUrlHostname` alone uses the one-argument `URL` constructor, which throws on any
 * relative input - so every client with a relative config URL (explicitly supported)
 * reported no request host at all. That silently disabled redirect detection for them:
 * `redirected` compares the request host against `responseURL`'s host and needs both,
 * so a relative config URL redirected to a captive portal on another host could never
 * be seen. That is precisely the 'intercepted' case the comparison exists to catch.
 */
export function safeRequestHostname(url: string) : string {
    const resolved = absoluteUrl(url);
    return safeUrlHostname(resolved || url);
}

/** A phase duration can never be negative; see the clamp note above. */
function phase(value: number) : number {
    return value > 0 ? value : 0;
}

/**
 * Fetch a known-good control resource on our own origin, after a config fetch
 * has already failed, to separate "this user has no network" from "this client's
 * config host is down". Never runs on the happy path, so it adds no request to a
 * successful page load.
 *
 * Needs NO bucket CORS policy, by construction: an <img> load is a no-cors request,
 * the same request class as the <script src> that loaded this bundle. (An earlier
 * draft used XHR and did depend on a CORS policy; this docstring described that
 * version long after `new Image()` replaced it. See CONTROL_PROBE_URL and
 * webpack.config.js for why an image, and REFERENCE.md §11 for why not `fetch`.)
 *
 * Returns 'blocked' rather than 'failed' when the request fails almost instantly AND
 * left no Resource Timing entry - the signature of a CSP `img-src` violation or a
 * blocking extension refusing the request locally, rather than a dead network.
 * 'blocked' is treated as no information, so a blocked probe degrades to useless
 * instead of actively wrong.
 *
 * Uses `new Image()` rather than HttpClient: it needs the elapsed time that
 * HttpClient's response contract does not expose, it must never retry, and it must
 * not be CORS-gated.
 */
export function probeControlOrigin() : Promise<ProbeResult> {
    // One arrow, one line. `resolve` exists only inside the Promise executor, so
    // capturing it is forced by the language; everything else is a ProbeSettler
    // method over instance fields (invariant #10).
    return new Promise<ProbeResult>(resolve => startProbe(resolve));
}

/**
 * Fire the probe image and hand every outcome to `settler`.
 *
 * Flat on purpose: the settle-once flag and the timeout handle are per-probe mutable
 * state, which is what instance fields are for.
 */
function startProbe(resolve: (result: ProbeResult) => void) : void {
    // Cache-bust: a cached image would "load" without touching the network and
    // report a reachable origin on a dead connection.
    const url = CONTROL_PROBE_URL + '?cb=' + probeNonce();
    const settler = new ProbeSettler(resolve, startTimer(), url);
    try {
        // `new Image()` is absent in non-browser hosts; degrade to no-information
        // rather than guessing. Old browsers simply lose the probe dimension.
        const image = new Image();
        image.onload = settler.onLoad.bind(settler);
        image.onerror = settler.onError.bind(settler);
        // The image has no native timeout, so bound it ourselves.
        settler.setTimeoutHandle(
            setTimeout(settler.onTimeout.bind(settler), CONTROL_PROBE_TIMEOUT_MS)
        );
        image.src = url;
    } catch (ignored) {
        // Image unavailable, or assigning src threw. We learn nothing about the
        // network from this, so claim nothing.
        settler.onUnavailable();
    }
}

/**
 * Resolves one probe exactly once, and never leaves the promise pending: a browser
 * may fire more than one event, or none at all.
 */
class ProbeSettler {

    private settled = false;
    private timeoutHandle: any = null;

    constructor(
        private readonly resolve: (result: ProbeResult) => void,
        private readonly bases: TimerBases,
        private readonly url: string,
    ) {}

    public setTimeoutHandle(handle: any) : void {
        this.timeoutHandle = handle;
    }

    public onLoad() : void {
        this.settle('ok');
    }

    public onTimeout() : void {
        this.settle('failed');
    }

    /** Image unavailable, or assigning src threw: we learn nothing. */
    public onUnavailable() : void {
        this.settle('skipped');
    }

    public onError() : void {
        // An <img> reports `error` for a blocked request just as it does for an
        // unreachable one. Separate them: a CSP img-src violation or a blocking
        // extension refuses the request locally, so it fails almost instantly AND
        // leaves no Resource Timing entry. Treat that as no-information rather than
        // claiming the user's network is down.
        const elapsed = elapsedMs(this.bases);
        // elapsed === -1 means unmeasurable, which must not read as "instant"
        if (elapsed >= 0 && elapsed < NO_ROUND_TRIP_MS
            && collectResourceTiming(this.url) === null) {
            this.settle('blocked');
            return;
        }
        this.settle('failed');
    }

    private settle(result: ProbeResult) : void {
        if (this.settled) {
            return;
        }
        this.settled = true;
        try {
            if (this.timeoutHandle !== null) {
                clearTimeout(this.timeoutHandle);
            }
        } catch (ignored) {}
        this.resolve(result);
    }

}

/** Cache-buster for the probe URL. Guarded: Date.now and Math.random can be stubbed. */
function probeNonce() : string {
    let stamp: number | null = null;
    let salt: number | null = null;
    try {
        stamp = coerceNumber(Date.now());
    } catch (ignored) {}
    try {
        salt = coerceNumber(Math.floor(Math.random() * 1e6));
    } catch (ignored) {}
    return String(stamp === null ? 0 : stamp) + '-' + String(salt === null ? 0 : salt);
}

/**
 * Derive a single failure class from the collected signals.
 *
 * Pure: no globals, no I/O, never throws. First matching rule wins, so the order
 * below IS the specification - see REFERENCE.md §11 for the confidence of each rule.
 *
 * No rule names a cause it cannot evidence. `blocked-csp` requires the browser to have
 * actually thrown a SecurityError; a request that merely never reached the network is
 * `no-round-trip`, which says what was observed and leaves the cause open. The
 * distinction is load-bearing: a rule keyed on absence of evidence converts every
 * upstream signal-destroying bug into a confident false diagnosis.
 *
 * Every rule here can actually fire. Two that could not - `dns` and `tls`, which
 * needed phase boundaries a `Timing-Allow-Origin` header the config hosts do not
 * send - were removed rather than left in place looking like coverage; see BACKLOG.md §10
 * to restore them with the header. The discriminating work is done by `online`, the
 * control probe, timeout-vs-error, and `bodyLooksHtml`. The phase timings are still
 * collected as human-facing detail in the `resource_timing` context.
 */
export function classifyFailure(s: FailureSignals) : FailureClass {
    // 1. Our own shape validation rejected it - conclusive, we ran the check.
    if (s.shapeFailed) {
        return 'bad-shape';
    }
    const ok2xx = s.status >= 200 && s.status < 300;
    // 2. HTML where JSON was expected: captive portal, ISP injection, or a
    //    maintenance page served with a 200. High signal in practice.
    //
    //    Both this rule and rule 3 require a body we actually READ. `null` means the
    //    response body was unreadable, and neither "it was HTML" nor "it was not" can
    //    be claimed about content nobody observed - so an unreadable body falls
    //    through to `unknown` rather than borrowing rule 3's verdict.
    if (s.parseFailed && ok2xx && s.bodyLooksHtml === true) {
        return 'intercepted';
    }
    // 3. Parsed as neither: a body dropped mid-flight, or simply not JSON.
    if (s.parseFailed && ok2xx && s.bodyLooksHtml === false) {
        return 'truncated-body';
    }
    // 4. Something else answered for us.
    if (s.redirected && ok2xx) {
        return 'intercepted';
    }
    // 5/6. A COMPLETED error response is direct proof the request reached the host
    //      and came back, so it outranks every heuristic below. These deliberately
    //      sit above `offline` and `network-down`: a 503 in hand cannot be explained
    //      by the user's network being down, and letting a slow or ad-blocked probe
    //      override it would file a real host outage under "not actionable by us".
    if (s.status >= 500) {
        return 'http-5xx';
    }
    if (s.status >= 400) {
        return 'http-4xx';
    }
    // 7. The browser reports no network interface. Negative-only: onLine === true
    //    proves nothing, which is why null must never be read as false. Sits
    //    above `timeout` deliberately - if the device has no network, a timeout
    //    is the user's problem and not the host's.
    if (s.online === false) {
        return 'offline';
    }
    // 8. Page unload mid-request. Not an incident.
    //
    //    Deliberately ABOVE the probe: a page unload aborts the config request and
    //    fails the image probe at the same time, for the same reason. Below the probe
    //    this filed every unload as a `network-down` incident - blaming the user's
    //    connection for a navigation. An abort is conclusive about our own request
    //    having been cancelled, so it outranks a probe-derived heuristic.
    if (s.handler === 'abort') {
        return 'aborted';
    }
    // 9. Our own origin is unreachable too, so the fault is client-side.
    if (s.probe === 'failed') {
        return 'network-down';
    }
    // 10. Conclusive that the budget was exceeded, NOT whose fault that is. Read
    //     alongside the visibility tag: a hidden tab is throttled by the browser.
    if (s.handler === 'timeout') {
        return 'timeout';
    }
    // 11. open()/send() threw synchronously - the request never left the machine.
    //     WHICH exception decides the class, and these must not be collapsed: a
    //     SecurityError is the client's Content-Security-Policy refusing the origin
    //     (actionable by the client), while a SyntaxError/InvalidStateError is a
    //     malformed config URL (actionable by US, and nothing to do with their CSP).
    //     Returns rather than falling through, because rule 13 below would otherwise
    //     re-label these `blocked-csp` anyway - a synchronous throw is always instant
    //     and always leaves no timing entry.
    if (s.handler === 'throw') {
        if (s.thrownName === 'SecurityError') {
            return 'blocked-csp';
        }
        // '' means the name itself was unreadable: we know it threw, not why.
        return s.thrownName ? 'request-rejected' : 'unknown';
    }
    const timing = s.timing;
    const phasesUsable = !!timing && !timing.opaque;
    // 12. Reached the network but produced no readable response: a missing or
    //     invalid Access-Control-Allow-Origin, or a blocking extension.
    //
    //     Requires phasesUsable. transferSize is zeroed for EVERY cross-origin entry
    //     without Timing-Allow-Origin, so without this gate an ordinary connection
    //     reset mid-download would be reported as a CORS misconfiguration - reading a
    //     zeroed field as evidence, which this module forbids. An opaque entry is
    //     genuinely ambiguous here and falls through to 'unknown'.
    //
    //     `transferSize === 0` must be a MEASURED zero: an unreadable getter reports
    //     null, and null is not evidence of an empty response. Reading it as one let a
    //     single hostile field fabricate a CORS misconfiguration - the same mistake
    //     rule 13 used to make with missing timing data.
    if (s.status === 0 && phasesUsable && timing.duration > 0 && timing.transferSize === 0) {
        return 'cors-or-blocked';
    }
    // 13. The request never made a network round trip: it failed too fast to have
    //     made one, and left no Resource Timing entry. Something refused it locally -
    //     a CSP directive, a blocking extension, an enterprise proxy plugin.
    //
    //     This rule fires on the ABSENCE of evidence, so it deliberately does NOT
    //     name a cause. It used to return `blocked-csp`, and that was the single
    //     biggest source of confidently-wrong reports in this module: every upstream
    //     bug that destroyed a signal - an unmeasured elapsed time, a timing summary
    //     lost to one hostile getter, a relative URL that never matched its own entry -
    //     produced exactly this input shape and was therefore reported as an
    //     accusation about the client's Content-Security-Policy. Five separate bugs,
    //     one wrong answer.
    //
    //     `blocked-csp` now requires positive evidence (rule 11's SecurityError). A
    //     rule that fires on absence of evidence must describe what we observed, not
    //     what we suspect - see REFERENCE.md §11 invariant 8.
    //
    //     elapsedMs === -1 means the duration was unmeasurable, which must NOT satisfy
    //     "failed instantly".
    if (s.status === 0 && timing === null
        && s.elapsedMs >= 0 && s.elapsedMs < NO_ROUND_TRIP_MS) {
        return 'no-round-trip';
    }
    return 'unknown';
}

/** Tag values must be low-cardinality strings; null/'' are dropped, not sent. */
function putTag(tags: Record<string, string>, name: string, value: string | null) : void {
    if (value) {
        tags[name] = value;
    }
}

function boolTag(value: boolean | null) : string {
    return value === null ? 'unknown' : (value ? 'true' : 'false');
}

/**
 * Assemble the Sentry payload for a failed config fetch: low-cardinality tags
 * for searching and charting, plus contexts carrying the detail.
 *
 * Wholly guarded - returns {} rather than throwing, because a diagnostics bug
 * must never cost us the underlying report.
 */
export function buildFailureReport(
    url: string,
    error: any,
    degradation: Degradation,
    configKind: string,
    probe: ProbeResult,
) : TelemetryScopeData {
    try {
        const failure = httpFailureOf(error);
        const attempts = failure ? failure.attempts : [];
        const last = attempts.length ? attempts[attempts.length - 1] : null;
        const safeUrl = stripQuery(url);
        // Resolved, so a relative config URL reports the host it was actually fetched
        // from rather than nothing. For those clients that host IS the client's own
        // site, so it is collapsed to a literal below before it reaches the
        // fingerprint - accurate as a searchable tag, bounded as a grouping key.
        const configHost = safeRequestHostname(url);
        const timing = collectResourceTiming(url);
        const connection = safeConnectionInfo();
        const online = safeNavigatorOnLine();
        // Only claim a redirect when BOTH hostnames are known and differ. A relative
        // config URL has no host, and treating that absence as "different" made every
        // attempt look intercepted and collapsed all such clients into one fingerprint.
        const finalHost = last ? safeUrlHostname(last.responseURL) : '';
        const redirected = !!finalHost && !!configHost && finalHost !== configHost;
        // A relative config URL is served by the client's own site. Worth stating as a
        // tag, but it must not reach the fingerprint: config_host would then be the
        // client hostname, opening one Sentry issue per client site - the exact thing
        // the fingerprint comment below excludes client_hostname to avoid.
        const sameOrigin = !!configHost && configHost === safeLocationHostname();

        const signals: FailureSignals = {
            // 'none', never a guess: with no attempt log we do not know which
            // handler fired, and defaulting to 'throw' would fabricate a CSP block.
            handler: last ? last.handler : 'none',
            thrownName: last ? last.thrownName : '',
            status: last ? last.status : 0,
            // -1, not 0: 0 is a measured "failed instantly" and satisfies rule 15's
            // no-round-trip test, which would fabricate a `blocked-csp` diagnosis for a
            // failure we have no attempt log for. -1 is the established unmeasurable
            // sentinel (see HttpAttempt.ms and attachStatusFailure).
            elapsedMs: last ? last.ms : -1,
            // null when the body was unreadable. `bytes === -1` is the attempt log's
            // documented "could not see the body", so it is the same fact stated once.
            bodyLooksHtml: last ? (last.bytes === -1 ? null : last.bodyLooksHtml) : null,
            redirected: redirected,
            online: online,
            visibility: last ? last.visEnd : safeVisibilityState(),
            timing: timing,
            probe: probe,
            parseFailed: isParseFailure(error),
            shapeFailed: isShapeFailure(error),
        };
        const failureClass = classifyFailure(signals);

        const tags: Record<string, string> = {};
        putTag(tags, 'failure_class', failureClass);
        putTag(tags, 'config_host', configHost);
        putTag(tags, 'config_origin', sameOrigin ? 'same-origin' : 'cross-origin');
        putTag(tags, 'config_kind', configKind);
        putTag(tags, 'degradation', degradation);
        putTag(tags, 'http_status', String(signals.status));
        putTag(tags, 'xhr_handler', signals.handler);
        // Low cardinality - a DOMException name, from a small fixed set. Dropped when
        // empty, so it appears only on attempts that actually threw.
        putTag(tags, 'thrown_name', signals.thrownName);
        putTag(tags, 'attempt_count', attempts.length ? String(attempts.length) : null);
        putTag(tags, 'online', boolTag(online));
        putTag(tags, 'effective_type', connection.effectiveType || 'unknown');
        putTag(tags, 'visibility', signals.visibility || 'unknown');
        putTag(tags, 'probe', probe);
        putTag(tags, 'timing', timing ? (timing.opaque ? 'opaque' : 'full') : 'absent');
        putTag(tags, 'redirected', last ? (redirected ? 'true' : 'false') : 'unknown');

        const contexts: Record<string, Record<string, any>> = {
            http_failure: {
                url: safeUrl,
                attempt_count: attempts.length,
                attempts: attempts,
            },
            connection: {
                effective_type: connection.effectiveType,
                rtt: connection.rtt,
                downlink: connection.downlink,
                save_data: connection.saveData,
            },
        };
        if (timing) {
            contexts.resource_timing = timing as any;
        }

        return {
            tags: tags,
            contexts: contexts,
            // Excludes the client hostname on purpose: including it would open one
            // Sentry issue per client site and make every new client look like a
            // new issue. The client_hostname TAG already gives filtering for free.
            fingerprint: [
                'config-fetch',
                degradation,
                failureClass,
                sameOrigin ? 'same-origin' : (configHost || 'unknown'),
            ],
        };
    } catch (ignored) {
        return {};
    }
}
